import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import UnarchiveIcon from "@mui/icons-material/UnarchiveOutlined";
import { Button, Checkbox } from "@mui/joy";
import { Avatar, Box, IconButton, Paper, Tooltip } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  currencyFormatter,
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Position_Entity } from "../../entities/position";
import { Talent_Entity } from "../../entities/talent";
import { useUpdateTalentMutation } from "../../redux/talent/talentEndpoints";
import {
  setFormOpen,
  setSelectedTalentID,
  setShowArchivedTalents,
  showArchivedTalents,
} from "../../redux/filmsReducer";
import { usePositions } from "../../redux/position/positionHooks";
import { useTalents } from "../../redux/talent/talentHooks";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import TalentAvatar from "../talent/talentsList/talentAvatar";

/**
 * Talent
 *
 * @returns {null} Talent
 */
export default function Talents() {
  const showArchived = useSelector(showArchivedTalents);
  const { talents: _talents } = useTalents();
  const talents = _talents?.filter((p) => {
    if (!showArchived) {
      return p.active;
    } else return true;
  });
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { positions } = usePositions();
  const [updateTalent] = useUpdateTalentMutation();

  const handleClick = (id: number) => {
    dispatch(setSelectedTalentID(id));
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        getCellClassName={(c) => (c.field === "id" ? "cellReadOnly" : "")}
        getRowClassName={(p) => (p.row.active ? "" : "archived")}
        apiRef={apiRef}
        onRowClick={(p) => handleClick(p.row.id)}
        rows={talents ?? []}
        columns={talentColumns(dispatch, positions ?? [], confirm, updateTalent)}
        getDetailPanelHeight={() => 100}
        showToolbar
        slots={{
          toolbar: TalentToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          cell: { tabIndex: 1 },
        }}
      />
    </Paper>
  );
}

function TalentToolbar() {
  const dispatch = useDispatch();
  const showArchived = useSelector(showArchivedTalents);
  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 1,
          gap: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Button
          variant="soft"
          size="sm"
          onClick={() =>
            dispatch(setFormOpen({ formID: "talent", isOpen: true }))
          }
        >
          + New Talent
        </Button>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
        <Checkbox
          size="sm"
          variant="outlined"
          label="Show Archived"
          onChange={(e) => dispatch(setShowArchivedTalents(e.target.checked))}
          checked={showArchived}
        ></Checkbox>
      </Box>
    </Toolbar>
  );
}

const talentColumns = (
  dispatch: Dispatch,
  positions: Position_Entity[],
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  updateTalent: (args: { id: number; body: Partial<Talent_Entity> }) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      type: "string",
      width: 40,
    },
    {
      field: "code",
      headerName: "",
      width: 30,
      type: "string",
      renderCell: (p) => <TalentAvatar talent={p.row} size={28} />,
    },
    {
      field: "favorite",
      headerName: "",
      width: 50,
      type: "boolean",
      renderCell: (p: any) => (
        <IconButton
          size="small"
          aria-label={p.row.favorite ? "Unfavorite" : "Favorite"}
          onClick={(e) => {
            // A row click opens the profile panel; the star shouldn't.
            e.stopPropagation();
            updateTalent({
              id: p.row.id,
              body: { favorite: !p.row.favorite },
            });
          }}
        >
          {p.row.favorite ? (
            <StarIcon fontSize="small" sx={{ color: "#FFC107" }} />
          ) : (
            <StarBorderIcon fontSize="small" />
          )}
        </IconButton>
      ),
    },
    {
      field: "firstName",
      headerName: "First Name",
      type: "string",
    },
    {
      field: "lastName",
      headerName: "Last Name",
      type: "string",
    },
    {
      field: "birthDate",
      headerName: "Birthdate",
      type: "string",
      width: 130,
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        if (value === "0001-01-01T00:00:00Z") return "Not set";
        const now = moment();
        const date = moment(value);
        if (date.isSame(now, "d")) {
          return `🎉 ${date.format("MM/DD/YYYY")}`;
        }
        return `${date.format("MM/DD/YYYY")}`;
      },
    },
    {
      field: "positionID",
      headerName: "Position",
      type: "string",
      width: 200,
      valueFormatter: (valueOrParams: any) =>
        positions.find((t) => t.id === (valueOrParams?.value ?? valueOrParams))
          ?.name ?? "",
    },
    {
      field: "baseHourlyRate",
      headerName: "Base Rate",
      headerAlign: "center",
      align: "right",
      width: 100,
      type: "number",
      valueGetter: (_value: any, row: any) =>
        (row.baseHourlyRate ?? 0) / 10000,
      // No rate on file is not a rate of nothing.
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return value ? `${currencyFormatter.format(value)}/h` : "—";
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      type: "string",
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 100,
      type: "string",
    },
    {
      field: "dropbox",
      headerName: " ",
      headerAlign: "center",
      align: "center",
      width: 50,
      type: "string",
      renderCell: (p) => {
        if (p.value) {
          return (
            <Tooltip
              title={
                <span>
                  Open Dropbox
                  <i className="fa-solid fa-up-right-from-square pleft"></i>
                </span>
              }
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.open(
                    p.value.indexOf("http") >= 0
                      ? p.value
                      : `https://${p.value}`,
                    "_blank"
                  );
                }}
              >
                <i className="fa-brands fa-dropbox"></i>
              </IconButton>
            </Tooltip>
          );
        }
        return <Box />;
      },
    },
    {
      field: "google",
      headerName: " ",
      headerAlign: "center",
      align: "center",
      width: 50,
      type: "string",
      renderCell: (p) => {
        if (p.value) {
          return (
            <Tooltip
              title={
                <span>
                  Open Google Drive
                  <i className="fa-solid fa-up-right-from-square pleft"></i>
                </span>
              }
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.open(
                    p.value.indexOf("http") >= 0
                      ? p.value
                      : `https://${p.value}`,
                    "_blank"
                  );
                }}
              >
                <i className="fa-brands fa-google-drive"></i>
              </IconButton>
            </Tooltip>
          );
        }
        return <Box />;
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 50,
      cellClassName: "actions",
      getActions: (p: any) => [
        <GridActionsCellItem
          key="archive"
          showInMenu
          icon={p.row.active ? <ArchiveIcon /> : <UnarchiveIcon />}
          label={p.row.active ? "Archive" : "Unarchive"}
          onClick={() => {
            if (!p.row.active) {
              updateTalent({ id: p.row.id, body: { active: true } });
              return;
            }
            confirm({
              description: `${p.row.firstName} ${p.row.lastName} will drop out of the talent pickers. Their jobs and ledger history stay, and "Show Archived" brings them back.`,
            })
              .then(() => {
                updateTalent({ id: p.row.id, body: { active: false } });
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);
